import { Box, Paper, Stack, Typography } from '@mui/material';
import { MetricBasisChips } from './MetricBasisChips';
import { EmptyState } from './states';
import { formatGrosze } from '@/lib/format';
import type { DailyTrendPoint } from '@/lib/api/analytics';

/**
 * Seria dzienna sprzedaży.
 *
 * DLACZEGO POWSTAŁA DOPIERO TERAZ: backend liczył `charts.daily_trend` od początku i po prostu
 * ją WYRZUCAŁ — pole nie istniało w kontrakcie klienta, więc żaden ekran go nie renderował.
 * Analiza parytetu (O7) to wychwyciła; to najtańsza z czterech bezpiecznych wygranych.
 *
 * DLACZEGO BEZ BIBLIOTEKI WYKRESÓW: to jest szereg 30–90 punktów, a nie panel analityczny.
 * Słupki z `div`-ów nie wymagają nowej zależności, działają w druku i nie mają problemu
 * z dostępnością, który mają wykresy na `canvas`. Gdy pojawi się potrzeba zoomu i tooltipów
 * per seria, wtedy będzie moment na bibliotekę — nie wcześniej.
 *
 * OŚ CZASU: dzień DOSTAWY (ADR-003). Backend kubełkuje po `delivery_date`. Stary panel
 * kubełkował po dacie zakupu, jednocześnie tnąc okno po dacie dostawy — tej niespójności
 * świadomie nie kopiujemy.
 */
export function DailyTrendChart({
  points,
  title = 'Sprzedaż dzień po dniu',
  kartaId,
}: {
  points: DailyTrendPoint[];
  title?: string;
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15).
   *
   * Dlaczego atrybut, a nie dopasowanie po etykiecie: sprawdzenie 100 etykiet z inwentarza
   * pokazało, że tylko 66 rozstrzyga się do jednego miejsca. Tutaj domyślny tytuł
   * („Sprzedaż dzień po dniu") jest podmienialny propem `title`, więc etykieta widoczna na
   * ekranie zależy od wywołania — jako adres nadaje się jeszcze gorzej niż tekst stały.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   *
   * UWAGA O STANIE PUSTYM: przy zerowej serii komponent oddaje `EmptyState`, który jest
   * współdzielony z resztą aplikacji i nie przyjmuje identyfikatora — w tym stanie karta
   * pozostaje nieadresowalna. Nie obchodzę tego opakowaniem, bo to zmieniłoby układ DOM,
   * a stan pusty i tak nie niesie danych do sprawdzenia.
   */
  kartaId?: string;
}) {
  if (!points.length) {
    return <EmptyState title="Brak serii dziennej w tym oknie" />;
  }

  const max = Math.max(...points.map((p) => p.sales_grosze), 1);
  const suma = points.reduce((acc, p) => acc + p.sales_grosze, 0);
  const dniZeSprzedaza = points.filter((p) => p.sales_grosze > 0).length;

  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
        {/* Ranga jawnie: to JEST tytuł bloku, a `subtitle1` sam z siebie nie jest już nagłówkiem. */}
        <Typography variant="subtitle1" component="h3">
          {title}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' }} />
      </Stack>

      {/* Dni bez sprzedaży są w serii jako zera — to świadome. Luka w wykresie sugerowałaby
          brak danych, a tu chodzi o realny brak sprzedaży w dniu, który w serii jest. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        {points.length} dni w oknie · {dniZeSprzedaza} ze sprzedażą · suma {formatGrosze(suma)}
      </Typography>

      <Box
        role="img"
        aria-label={`Wykres słupkowy sprzedaży dziennej, ${points.length} dni, maksimum ${formatGrosze(max)}`}
        sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 140, mb: 1 }}
      >
        {points.map((p) => (
          <Box
            key={p.date}
            title={`${p.date}: ${formatGrosze(p.sales_grosze)} · ${p.orders} zam.`}
            sx={{
              flex: 1,
              minWidth: 2,
              height: `${Math.max((p.sales_grosze / max) * 100, p.sales_grosze > 0 ? 2 : 0)}%`,
              bgcolor: p.sales_grosze > 0 ? 'primary.main' : 'action.disabledBackground',
              borderRadius: '2px 2px 0 0',
              transition: 'opacity .15s',
              '&:hover': { opacity: 0.7 },
            }}
          />
        ))}
      </Box>

      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {points[0]!.date}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          maks. dzienna: {formatGrosze(max)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {points[points.length - 1]!.date}
        </Typography>
      </Stack>
    </Paper>
  );
}
